import * as widgetActions from '../actions/widget.actions';

import { Action, createReducer, on } from '@ngrx/store';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { feedUnloaded } from '../actions/feed.actions';
import { produce } from 'immer';

export interface WidgetViewConfig {
  label: string;
  path: string;
  icon: IconProp;
  selected: boolean;
}

export interface WidgetPreviewConfig {
  widgetPresetId: number;
  vizTypeId: number;
}

const widgetConfigurations: WidgetViewConfig[] = [
  {
    label: 'widget-actions.button.attached-pivots',
    path: 'pivots',
    icon: ['fas', 'cube'],
    selected: true,
  },
  {
    label: 'widget-actions.button.attached-filters',
    path: 'filters',
    icon: ['fas', 'tag'],
    selected: true,
  },
  {
    label: 'widget-actions.button.Attached-data-sources',
    path: 'datasources',
    icon: ['fas', 'database'],
    selected: true,
  },
];

/**
 * Client state only. The widget list itself is `injectObjectWidgetsQuery`, read
 * through `CurrentViewWidgetsService`; un-persisted layout lives in
 * `WidgetLayoutStagingService` and `is_visible` / `chart_options` in
 * `WidgetViewStateService`, since a refetch would revert them.
 *
 * `loadingWidgets` is the widget-area spinner a **bulk delete** raises; the list's
 * own loading comes from the query.
 */
export interface WidgetState {
  loadingWidgets: boolean;
  countSelectedWidgets: number;
  searchString: string;
  viewConfig: WidgetViewConfig[];
  selectedPreviewWidgetConfig: WidgetPreviewConfig;
  selectedPlaceholderWidgetId: string;
}

export const initialState: WidgetState = {
  loadingWidgets: false,
  countSelectedWidgets: 0,
  searchString: '',
  viewConfig: widgetConfigurations,
  selectedPreviewWidgetConfig: null,
  selectedPlaceholderWidgetId: null,
};

const _widgetReducer = createReducer(
  initialState,

  on(feedUnloaded, (state) => {
    return initialState;
  }),

  on(widgetActions.deleteWidgets, (state) => {
    return {
      ...state,
      loadingWidgets: true,
    };
  }),

  on(widgetActions.deleteWidgetsSuccess, (state) => {
    return {
      ...state,
      loadingWidgets: false,
    };
  }),

  on(widgetActions.deleteWidgetsFail, (state) => {
    return {
      ...state,
      loadingWidgets: false,
    };
  }),

  on(widgetActions.widgetMassOperationSuccess, (state) => {
    return {
      ...state,
      loadingWidgets: false,
    };
  }),

  on(widgetActions.searchWidgets, (state, { query }) => {
    return {
      ...state,
      searchString: query,
    };
  }),

  on(
    widgetActions.toggleWidgetViewOption,
    produce((draft, { optionName }) => {
      const option = draft.viewConfig.find((opt) => opt.label === optionName);
      if (option) {
        option.selected = !option.selected;
      }
    }),
  ),

  on(widgetActions.selectWidgetPlaceholderId, (state, { id }) => {
    return {
      ...state,
      selectedPlaceholderWidgetId: id,
    };
  }),

  // * the created widget took the placeholder's slot, so the selection is spent
  on(widgetActions.createWidgetSuccess, (state) => {
    return {
      ...state,
      selectedPlaceholderWidgetId: null,
    };
  }),
);

export function widgetReducer(state: WidgetState, action: Action) {
  return _widgetReducer(state, action);
}
